import { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { Layout, ConfigProvider } from 'antd';
import {
  PRODUCT_NAME,
  PRODUCT_CATEGORY,
  PRODUCT_TAGLINE,
  PRODUCT_SUPPORTING_COPY,
  BRAND_CAPABILITY_SLIDES,
  ORDAN_TEAL,
  WITHU_BRAND,
  type AuthBrand,
} from '@/constants/brand';

const { Content } = Layout;

interface AuthLayoutProps {
  brand?: AuthBrand;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ brand = 'ordan' }) => {
  const location = useLocation();
  const path = location.pathname;
  const isWithu = brand === 'withu';
  const brandName = isWithu ? WITHU_BRAND.name : PRODUCT_NAME;
  const authPrimary = isWithu ? WITHU_BRAND.navy : ORDAN_TEAL;
  const isLogin = path === '/login' || path === '/loginwithu';
  const isRegister = path === '/register';
  const isForgot = path === '/forgot-password';
  const isReset = path === '/reset-password';
  const isPrivacy = path === '/privacy';
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    if (!isWithu) return undefined;
    const previous = document.title;
    document.title = `${WITHU_BRAND.name} - Sign in`;
    return () => {
      document.title = previous;
    };
  }, [isWithu]);

  const pageChrome =
    isForgot
      ? {
          title: 'Reset access',
          subtitle: 'We will email a secure link if an account matches.',
        }
      : isReset
        ? {
            title: 'Choose a new password',
            subtitle: 'Pick a strong password you have not used here before.',
          }
        : null;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    const id = window.setInterval(() => {
      setSlideIndex((i) => (i + 1) % BRAND_CAPABILITY_SLIDES.length);
    }, 8000);
    return () => window.clearInterval(id);
  }, []);

  const slide = BRAND_CAPABILITY_SLIDES[slideIndex];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: authPrimary,
          borderRadiusLG: 10,
          fontFamily: '"Source Sans 3", "Segoe UI", sans-serif',
        },
        components: {
          Button: {
            controlHeightLG: 48,
            fontWeightStrong: 600,
            borderRadius: 10,
          },
          Input: {
            borderRadius: 10,
            controlHeightLG: 48,
          },
        },
      }}
    >
      <Layout className={`auth-shell${isWithu ? ' auth-shell--withu' : ''}`}>
        <Content className="auth-shell__content" role="main" aria-label="Sign in or register">
          <div className={`auth-shell__stage${isPrivacy ? ' auth-shell__stage--wide-rail' : ''}`}>
            <section className="auth-hero" aria-label={`${brandName} brand`}>
              <div className="auth-hero__atmosphere" aria-hidden />
              <div className="auth-hero__grid" aria-hidden />
              <div className="auth-hero__glow" aria-hidden />

              <div className="auth-hero__composition">
                <p className="auth-hero__eyebrow">{PRODUCT_CATEGORY}</p>
                <h1 className="auth-hero__brand">
                  {isWithu ? (
                    <img className="auth-hero__logo" src={WITHU_BRAND.logoSrc} alt={WITHU_BRAND.name} />
                  ) : (
                    PRODUCT_NAME
                  )}
                </h1>
                <p className="auth-hero__headline">{PRODUCT_TAGLINE}</p>
                <p className="auth-hero__lede">{PRODUCT_SUPPORTING_COPY}</p>

                <div
                  className="auth-hero__tips"
                  role="region"
                  aria-label="Platform capabilities"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <div key={slide.id} className="auth-hero__tip-block">
                    <div className="auth-hero__tip-meta">
                      <span className="auth-hero__tip-step">{slide.eyebrow}</span>
                      <span className="auth-hero__tip-sep" aria-hidden="true">·</span>
                      <span className="auth-hero__tip-category">{slide.category}</span>
                    </div>
                    <h2 className="auth-hero__tip-title">{slide.title}</h2>
                    <p className="auth-hero__tip-body">{slide.description}</p>
                  </div>
                  <div className="auth-hero__tip-dots" role="tablist" aria-label="Capability carousel">
                    {BRAND_CAPABILITY_SLIDES.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        role="tab"
                        aria-selected={i === slideIndex}
                        aria-label={`${s.eyebrow} ${s.category}: ${s.title}`}
                        className={`auth-hero__tip-dot${i === slideIndex ? ' is-active' : ''}`}
                        onClick={() => setSlideIndex(i)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="auth-rail" aria-label="Credentials">
              <div className="auth-rail__inner">
                <header className="auth-rail__header">
                  <span className="auth-rail__mark">
                    {isWithu ? (
                      <img className="auth-rail__logo" src={WITHU_BRAND.logoSrc} alt={WITHU_BRAND.name} />
                    ) : (
                      PRODUCT_NAME
                    )}
                  </span>
                  <p className="auth-rail__tagline">{PRODUCT_CATEGORY}</p>
                </header>

                <div
                  className={`auth-form-region${isLogin || isForgot || isReset ? ' auth-form-region--login' : ''}${isPrivacy ? ' auth-form-region--document' : ''}`}
                >
                  {isLogin && (
                    <>
                      <h2 className="auth-page-title">Sign in</h2>
                      <p className="auth-page-subtitle">
                        Sign in with your organization credentials.
                      </p>
                    </>
                  )}
                  {isRegister && (
                    <>
                      <h2 className="auth-page-title">Create account</h2>
                      <p className="auth-page-subtitle">Register with your organization credentials.</p>
                    </>
                  )}
                  {pageChrome && (
                    <>
                      <h2 className="auth-page-title">{pageChrome.title}</h2>
                      <p className="auth-page-subtitle">{pageChrome.subtitle}</p>
                    </>
                  )}
                  <Outlet />
                </div>

                <footer className="auth-footer">
                  <Link to="/privacy" className="auth-footer__link">
                    Privacy Policy
                  </Link>
                  <span className="auth-footer__copy">
                    © {new Date().getFullYear()} {brandName}. All rights reserved.
                  </span>
                </footer>
              </div>
            </section>
          </div>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default AuthLayout;
